import React, { memo } from 'react';
import { Flow } from '@/types/Document';
import PhaseRenderer from './PhaseRenderer';
import ItemActionMenu from './ItemActionMenu';

interface FlowRendererProps {
  flow: Flow;
  flowIndex: number;
  editMode: boolean;
  collapsedPhases: Set<string>;
  openMenuPath: string | null;
  setOpenMenuPath: React.Dispatch<React.SetStateAction<string | null>>;
  togglePhaseCollapse: (flowIndex: number, phaseIndex: number) => void;
  handleInputChange: (
    path: (string | number)[],
    value: string,
    type?: 'flow' | 'phase' | 'step' | 'block'
  ) => void;
  handleDeleteItem: (itemPath: string) => void;
  handleAddItem: (parentPath: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  handleChangeItemType: (itemPath: string, newType: 'phase' | 'step') => void;
  handleCreateStepFromSelection: (selection: string, blockPath: string) => void;
}

const FlowRenderer: React.FC<FlowRendererProps> = memo(({
  flow,
  flowIndex,
  editMode,
  collapsedPhases,
  openMenuPath,
  setOpenMenuPath,
  togglePhaseCollapse,
  handleInputChange,
  handleDeleteItem,
  handleAddItem,
  handleChangeItemType,
  handleCreateStepFromSelection,
}) => {
  const flowPath = `flow-${flowIndex}`;
  const isMenuOpen = openMenuPath === flowPath;

  return (
    <div className="space-y-4">
      {/* Flow Header */}
      {flow.Flow && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div className="flex-grow mr-4">
              {editMode ? (
                <>
                  <input
                    type="text"
                    value={flow.Flow.name}
                    onChange={(e) => 
                      handleInputChange([flowIndex, 'Flow', 'name'], e.target.value, 'flow')
                    }
                    className="text-lg font-semibold text-gray-800 w-full p-1 mb-1 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {flow.Flow.description !== undefined && (
                    <textarea
                      value={flow.Flow.description}
                      onChange={(e) => 
                        handleInputChange([flowIndex, 'Flow', 'description'], e.target.value, 'flow')
                      }
                      className="text-sm text-gray-600 w-full p-1 border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      rows={2}
                    />
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-800">
                    <span className="font-bold">Flow:</span> {flow.Flow.name}
                  </h3>
                  {flow.Flow.description && (
                    <p className="text-sm text-gray-600 mt-1">{flow.Flow.description}</p>
                  )}
                </>
              )}
            </div>
            <div className="flex space-x-1 text-gray-400 relative flex-shrink-0">
              {editMode && (
                <button 
                  type="button"
                  onClick={() => setOpenMenuPath(isMenuOpen ? null : flowPath)}
                  className="p-1 rounded hover:bg-gray-200"
                  title="Flow Actions"
                  aria-label="Flow actions menu"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-5 w-5" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" 
                    />
                  </svg>
                </button>
              )}
              {isMenuOpen && (
                <ItemActionMenu 
                  itemPath={flowPath}
                  itemType="flow"
                  onClose={() => setOpenMenuPath(null)}
                  onAddItem={handleAddItem}
                  onDeleteItem={handleDeleteItem}
                  onChangeItemType={handleChangeItemType}
                  onCreateStepFromSelection={handleCreateStepFromSelection}
                />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Phases Rendering */}
      {flow.phases && flow.phases.length > 0 && (
        <div className="space-y-4 pl-4 border-l-2 border-gray-100">
          {flow.phases.map((phase, phaseIndex) => (
            <PhaseRenderer
              key={`phase-${flowIndex}-${phaseIndex}`}
              phase={phase}
              flowIndex={flowIndex}
              phaseIndex={phaseIndex}
              editMode={editMode}
              isCollapsed={collapsedPhases.has(`${flowIndex}-${phaseIndex}`)}
              openMenuPath={openMenuPath}
              setOpenMenuPath={setOpenMenuPath}
              togglePhaseCollapse={togglePhaseCollapse}
              handleInputChange={handleInputChange}
              handleDeleteItem={handleDeleteItem}
              handleAddItem={handleAddItem}
              handleChangeItemType={handleChangeItemType}
              handleCreateStepFromSelection={handleCreateStepFromSelection}
            />
          ))}
        </div>
      )}
    </div>
  );
});

FlowRenderer.displayName = 'FlowRenderer';

export default FlowRenderer;
